import React, { useEffect, useMemo, useState } from "react";
import { usePageHeader } from "../contexts/PageHeaderContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Wrench, Package, Search, Plus, X, Trash2, Loader2, AlertTriangle, TrendingUp, Pencil, ArrowDownUp, Crown } from "lucide-react";
import { pricebookApi } from "../lib/api";
import { serviceComputedCost, serviceMaterialCost, laborCost, profitOf } from "../lib/jobCost";
import { useAuth } from "../contexts/AuthContext";
import MembershipsTab from "../components/pricebook/MembershipsTab";

const UNITS = ["each", "hr", "sq ft", "lb", "ft", "gal"];
const DEFAULT_LOW_STOCK_THRESHOLD = 10;

function cleanBom(bom) {
  return (Array.isArray(bom) ? bom : [])
    .filter((b) => b && b.pricebookId && Number(b.qty) > 0)
    .map((b) => ({ pricebookId: b.pricebookId, qty: Number(b.qty) }));
}

export function effectiveLowStockThreshold(item) {
  if (item && item.lowStockThreshold != null && item.lowStockThreshold !== "") {
    return Number(item.lowStockThreshold);
  }
  return DEFAULT_LOW_STOCK_THRESHOLD;
}

export function isLowStock(item) {
  if (!item || item.kind !== "material") return false;
  return (Number(item.stockOnHand) || 0) <= effectiveLowStockThreshold(item);
}

function money(n) {
  return `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function marginPct(price, cost) {
  if (!price || price <= 0) return 0;
  return ((price - cost) / price) * 100;
}

export default function Pricebook() {
  const { user, hasModule } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("service"); // "service" | "material"
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [adjusting, setAdjusting] = useState(null); // item being adjusted

  const isAdmin = user?.role === "admin";

  const reload = async () => {
    setLoading(true);
    try {
      const list = await pricebookApi.list();
      setItems(list);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load pricebook");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const services = useMemo(() => items.filter((i) => i.kind === "service"), [items]);
  const materials = useMemo(() => items.filter((i) => i.kind === "material"), [items]);
  const tabItems = tab === "service" ? services : materials;

  const categories = useMemo(() => {
    const set = new Set(tabItems.map((i) => i.category || "Uncategorized"));
    return ["All", ...Array.from(set).sort()];
  }, [tabItems]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tabItems.filter((i) => {
      if (categoryFilter !== "All" && (i.category || "Uncategorized") !== categoryFilter) return false;
      if (!q) return true;
      return `${i.id} ${i.name} ${i.sku || ""} ${i.description || ""}`.toLowerCase().includes(q);
    });
  }, [tabItems, search, categoryFilter]);

  // KPIs
  const kpis = useMemo(() => {
    const lowStock = materials.filter((m) => isLowStock(m)).length;
    const inventoryValue = materials.reduce(
      (s, m) => s + (Number(m.stockOnHand) || 0) * (Number(m.cost) || 0),
      0,
    );
    return {
      services: services.length,
      materials: materials.length,
      lowStock,
      inventoryValue,
    };
  }, [services, materials]);

  usePageHeader({ title: "Pricebook & Inventory", subtitle: `${items.length} items in catalog` });

  return (
    <>
      <div className="px-4 md:px-6 py-4 space-y-4">
        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <Kpi icon={Wrench} label="Services" value={kpis.services} accent="text-blue-700 bg-blue-50" testid="kpi-services" />
          <Kpi icon={Package} label="Materials" value={kpis.materials} accent="text-violet-700 bg-violet-50" testid="kpi-materials" />
          <Kpi
            icon={AlertTriangle}
            label="Low stock"
            value={kpis.lowStock}
            accent={kpis.lowStock > 0 ? "text-rose-700 bg-rose-50" : "text-slate-500 bg-slate-100"}
            testid="kpi-low-stock"
          />
          <Kpi icon={TrendingUp} label="Inventory value" value={money(kpis.inventoryValue)} accent="text-emerald-700 bg-emerald-50" testid="kpi-inventory-value" />
        </div>

        {/* Tabs + toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-1 w-full sm:w-auto">
            <TabBtn active={tab === "service"} onClick={() => { setTab("service"); setCategoryFilter("All"); }} icon={Wrench} label="Services" count={services.length} testid="tab-services" />
            <TabBtn active={tab === "material"} onClick={() => { setTab("material"); setCategoryFilter("All"); }} icon={Package} label="Materials" count={materials.length} testid="tab-materials" />
          </div>
          {tab !== "membership" && (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 sm:flex-initial min-w-0">
              <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${tab}s…`}
                data-testid="pricebook-search"
                className="h-9 pl-8 pr-3 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 w-full sm:w-56"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              data-testid="pricebook-category-filter"
              className="h-9 px-2.5 rounded-lg border border-slate-200 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
            >
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {isAdmin && (
              <Button
                onClick={() => setCreating(true)}
                data-testid="pricebook-new-btn"
                className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px] font-semibold"
              >
                <Plus className="h-4 w-4 mr-1.5" /> New {tab === "service" ? "Service" : "Material"}
              </Button>
            )}
          </div>
          )}
        </div>

        {/* Table — desktop/tablet */}
        <div className="hidden md:block bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50/60 border-b border-slate-200">
              <tr className="text-left text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                <th className="px-4 py-2.5 w-24">ID</th>
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5 w-32">Category</th>
                <th className="px-4 py-2.5 w-20">SKU</th>
                <th className="px-4 py-2.5 w-20 text-right">Price</th>
                <th className="px-4 py-2.5 w-20 text-right">Cost</th>
                <th className="px-4 py-2.5 w-20 text-right">Margin</th>
                {tab === "material" && <th className="px-4 py-2.5 w-24 text-right">Stock</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={tab === "material" ? 8 : 7} className="px-4 py-10 text-center text-slate-400">
                  <Loader2 className="h-5 w-5 animate-spin inline" />
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={tab === "material" ? 8 : 7} className="px-4 py-10 text-center text-slate-400 italic">
                  No items found.
                </td></tr>
              ) : filtered.map((i) => {
                const lowStock = isLowStock(i);
                const pct = marginPct(i.unitPrice, i.cost);
                return (
                  <tr
                    key={i.id}
                    onClick={() => setSelected(i)}
                    data-testid={`pricebook-row-${i.id}`}
                    className="hover:bg-blue-50/40 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-2.5 font-mono text-[11.5px] text-slate-500">{i.id}</td>
                    <td className="px-4 py-2.5 text-slate-900 font-medium">
                      {i.name}
                      {i.description && <div className="text-[11px] text-slate-500 truncate max-w-[400px]">{i.description}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{i.category || <span className="italic text-slate-400">—</span>}</td>
                    <td className="px-4 py-2.5 text-slate-500 font-mono text-[11.5px]">{i.sku || "—"}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-slate-900">{money(i.unitPrice)}<span className="text-slate-400 text-[10.5px] font-normal">/{i.unit}</span></td>
                    <td className="px-4 py-2.5 text-right text-slate-600">{money(i.cost)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className={`font-semibold ${pct >= 50 ? "text-emerald-700" : pct >= 25 ? "text-amber-700" : "text-rose-700"}`}>
                        {pct.toFixed(0)}%
                      </span>
                    </td>
                    {tab === "material" && (
                      <td className="px-4 py-2.5 text-right">
                        <span className={`font-bold ${lowStock ? "text-rose-700" : "text-slate-900"}`}>
                          {Number(i.stockOnHand).toFixed(0)}
                        </span>
                        {lowStock && <AlertTriangle className="h-3 w-3 text-rose-500 inline ml-1" />}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Card list — mobile: name + description only, ID/other columns visible in detail */}
        <div className="md:hidden bg-white rounded-xl border border-slate-200 overflow-hidden divide-y divide-slate-100" data-testid="pricebook-mobile-list">
          {loading ? (
            <div className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-10 text-center text-slate-400 italic">No items found.</div>
          ) : filtered.map((i) => (
            <button
              key={i.id}
              onClick={() => setSelected(i)}
              data-testid={`pricebook-row-mobile-${i.id}`}
              className="w-full text-left px-4 py-3 active:bg-blue-50/40 transition-colors"
            >
              <div className="font-bold text-[14px] text-slate-900">{i.name}</div>
              {i.description && <div className="text-[12px] text-slate-500 mt-0.5">{i.description}</div>}
            </button>
          ))}
        </div>

        {tab === "service" && hasModule("memberships") && (
          <div className="pt-4 mt-2 border-t border-slate-200" data-testid="pricebook-memberships-section">
            <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2 mb-3 bg-white rounded-lg px-3 py-2.5 border border-slate-200 md:bg-transparent md:border-0 md:px-0 md:py-0 md:rounded-none">
              <div className="flex items-center gap-2">
                <Crown className="h-4 w-4 text-violet-600" />
                <h2 className="text-[14px] font-bold text-slate-900">Memberships</h2>
              </div>
              <span className="text-[12px] text-slate-500">— sold via “Pick from Pricebook” on estimates</span>
            </div>
            <MembershipsTab isAdmin={isAdmin} />
          </div>
        )}

        {/* Dedicated height-based spacer (not padding) so content clears the fixed mobile bottom nav */}
        <div aria-hidden="true" className="md:hidden shrink-0" style={{ height: "calc(5rem + env(safe-area-inset-bottom))" }} />
      </div>

      {creating && (
        <ItemFormDialog
          mode="create"
          initialKind={tab}
          materials={materials}
          onClose={() => setCreating(false)}
          onSaved={(item) => {
            setItems((prev) => [...prev, item]);
            setCreating(false);
            setSelected(item);
          }}
        />
      )}
      {selected && !adjusting && (
        <ItemFormDialog
          mode="edit"
          item={selected}
          canEdit={isAdmin}
          materials={materials}
          onClose={() => setSelected(null)}
          onSaved={(item) => {
            setItems((prev) => prev.map((i) => (i.id === item.id ? item : i)));
            setSelected(item);
          }}
          onDeleted={(id) => {
            setItems((prev) => prev.filter((i) => i.id !== id));
            setSelected(null);
          }}
          onAdjustStock={() => setAdjusting(selected)}
        />
      )}
      {adjusting && (
        <AdjustStockDialog
          item={adjusting}
          onClose={() => setAdjusting(null)}
          onAdjusted={(item) => {
            setItems((prev) => prev.map((i) => (i.id === item.id ? item : i)));
            setSelected(item);
            setAdjusting(null);
          }}
        />
      )}
    </>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, count, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 sm:flex-initial flex items-center justify-center gap-1.5 h-8 px-3 rounded-md text-[12.5px] font-semibold transition-colors ${
        active ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
      <span className={`text-[10.5px] px-1.5 py-0.5 rounded-full ${active ? "bg-blue-100 text-blue-700" : "bg-slate-200 text-slate-600"}`}>
        {count}
      </span>
    </button>
  );
}

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 md:p-4 flex items-center gap-2.5 md:gap-3 min-w-0" data-testid={testid}>
      <div className={`h-9 w-9 md:h-10 md:w-10 rounded-lg flex items-center justify-center shrink-0 ${accent}`}>
        <Icon className="h-4 w-4 md:h-5 md:w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] md:text-[11px] uppercase tracking-wider font-semibold text-slate-500 leading-tight">{label}</div>
        <div className="text-[16px] md:text-[20px] font-bold text-slate-900 leading-tight">{value}</div>
      </div>
    </div>
  );
}

function LaborEditor({ form, canEdit = true, onChange }) {
  const type = form.laborType || "none";
  const computed = type === "hourly"
    ? (Number(form.laborRate) || 0) * (Number(form.laborHours) || 0)
    : type === "flat" ? (Number(form.laborFlat) || 0) : 0;
  const opts = [
    { k: "none", label: "No labor" },
    { k: "hourly", label: "Hourly" },
    { k: "flat", label: "Flat" },
  ];
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-2.5 space-y-2.5" data-testid="labor-editor">
      <div className="flex gap-2">
        {opts.map((o) => (
          <button
            key={o.k}
            type="button"
            onClick={() => onChange({ laborType: o.k === "none" ? null : o.k })}
            data-testid={`labor-type-${o.k}`}
            disabled={!canEdit}
            className={`flex-1 h-9 rounded-md border text-[12.5px] font-semibold transition-colors ${
              type === o.k ? "bg-amber-100 border-amber-400 text-amber-800" : "bg-white border-slate-200 text-slate-600 hover:border-amber-300"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
      {type === "hourly" && (
        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Rate ($/hr)</label>
            <input
              type="number" min={0} step={0.01}
              value={form.laborRate}
              onChange={(e) => onChange({ laborRate: e.target.value })}
              data-testid="labor-rate"
              className={inputClass}
              disabled={!canEdit}
            />
          </div>
          <div>
            <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Est. hours</label>
            <input
              type="number" min={0} step={0.25}
              value={form.laborHours}
              onChange={(e) => onChange({ laborHours: e.target.value })}
              data-testid="labor-hours"
              className={inputClass}
              disabled={!canEdit}
            />
          </div>
        </div>
      )}
      {type === "flat" && (
        <div>
          <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Flat labor cost ($)</label>
          <input
            type="number" min={0} step={0.01}
            value={form.laborFlat}
            onChange={(e) => onChange({ laborFlat: e.target.value })}
            data-testid="labor-flat"
            className={inputClass}
            disabled={!canEdit}
          />
        </div>
      )}
      {type !== "none" && (
        <div className="text-[12px] text-amber-700 font-medium" data-testid="labor-computed">
          Labor cost: <span className="font-bold">${computed.toFixed(2)}</span>
          {type === "hourly" && " (rate × est. hours)"}
        </div>
      )}
    </div>
  );
}

function BomEditor({ bom = [], materials = [], canEdit = true, onChange }) {
  const [pick, setPick] = useState("");
  const byId = useMemo(() => {
    const m = {};
    (materials || []).forEach((x) => { m[x.id] = x; });
    return m;
  }, [materials]);
  const usedIds = new Set((bom || []).map((b) => b.pricebookId));
  const available = (materials || []).filter((m) => !usedIds.has(m.id));

  const add = () => {
    if (!pick) return;
    onChange([...(bom || []), { pricebookId: pick, qty: 1 }]);
    setPick("");
  };
  const setQty = (id, qty) => onChange((bom || []).map((b) => (b.pricebookId === id ? { ...b, qty } : b)));
  const remove = (id) => onChange((bom || []).filter((b) => b.pricebookId !== id));

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-2.5 space-y-2" data-testid="bom-editor">
      <p className="text-[11.5px] text-slate-500 leading-snug">
        Materials consumed from inventory when a job with this service is completed.
      </p>
      {(bom || []).length === 0 && (
        <div className="text-[12.5px] text-slate-400 italic py-1" data-testid="bom-empty">No materials attached yet.</div>
      )}
      {(bom || []).map((b) => {
        const mat = byId[b.pricebookId];
        return (
          <div key={b.pricebookId} className="flex items-center gap-2" data-testid={`bom-row-${b.pricebookId}`}>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-slate-800 truncate">{mat ? mat.name : b.pricebookId}</div>
              {mat && <div className="text-[11px] text-slate-400">{mat.id} · {Number(mat.stockOnHand).toFixed(0)} in stock</div>}
            </div>
            <input
              type="number"
              min={1}
              step={0.01}
              value={b.qty}
              onChange={(e) => setQty(b.pricebookId, e.target.value === "" ? "" : Number(e.target.value))}
              data-testid={`bom-qty-${b.pricebookId}`}
              className="h-8 w-20 rounded-md border border-slate-200 px-2 text-[13px] bg-white"
              disabled={!canEdit}
            />
            <span className="text-[11px] text-slate-400 w-8">{mat?.unit || ""}</span>
            {canEdit && (
              <button
                onClick={() => remove(b.pricebookId)}
                data-testid={`bom-remove-${b.pricebookId}`}
                className="h-8 w-8 rounded-md hover:bg-rose-50 flex items-center justify-center text-rose-500"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        );
      })}
      {canEdit && (
        <div className="flex items-center gap-2 pt-1">
          <select
            value={pick}
            onChange={(e) => setPick(e.target.value)}
            data-testid="bom-add-select"
            className="flex-1 h-9 rounded-md border border-slate-200 px-2 text-[13px] bg-white"
          >
            <option value="">+ Add a material…</option>
            {available.map((m) => (
              <option key={m.id} value={m.id}>{m.name} ({Number(m.stockOnHand).toFixed(0)} in stock)</option>
            ))}
          </select>
          <Button
            variant="outline"
            onClick={add}
            disabled={!pick}
            data-testid="bom-add-btn"
            className="h-9 text-[13px] gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" /> Add
          </Button>
        </div>
      )}
    </div>
  );
}

function ItemFormDialog({ mode, item, initialKind, canEdit = true, materials = [], onClose, onSaved, onDeleted, onAdjustStock }) {
  const isCreate = mode === "create";
  const [form, setForm] = useState(
    item
      ? { ...item, bom: Array.isArray(item.bom) ? item.bom : [], laborType: item.laborType || null,
          laborRate: item.laborRate ?? "", laborHours: item.laborHours ?? "", laborFlat: item.laborFlat ?? "",
          pieceRate: item.pieceRate ?? "" }
      : {
          kind: initialKind || "service",
          name: "",
          description: "",
          unit: "each",
          unitPrice: 0,
          cost: 0,
          taxable: initialKind === "material",
          category: "",
          sku: "",
          stockOnHand: 0,
          lowStockThreshold: "",
          memberExempt: false,
          memberPrice: "",
          pieceRate: "",
          bom: [],
          laborType: null,
          laborRate: "",
          laborHours: "",
          laborFlat: "",
        },
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const isServiceForm = isCreate ? form.kind === "service" : item?.kind === "service";
  const materialsById = useMemo(() => {
    const m = {};
    (materials || []).forEach((x) => { m[x.id] = x; });
    return m;
  }, [materials]);
  const laborForm = { laborType: form.laborType, laborRate: form.laborRate, laborHours: form.laborHours, laborFlat: form.laborFlat };
  const matCost = isServiceForm ? serviceMaterialCost({ bom: form.bom }, materialsById) : 0;
  const labCost = isServiceForm ? laborCost(laborForm) : 0;
  const computedServiceCost = isServiceForm ? serviceComputedCost({ bom: form.bom, ...laborForm }, materialsById) : (Number(form.cost) || 0);
  const { profit, pct } = profitOf(form.unitPrice, computedServiceCost);

  const submit = async () => {
    if (!form.name.trim()) { setError("Name is required"); return; }
    setError("");
    setBusy(true);
    try {
      if (isCreate) {
        const created = await pricebookApi.create({
          kind: form.kind,
          name: form.name.trim(),
          description: form.description || "",
          unit: form.unit || "each",
          unitPrice: Number(form.unitPrice) || 0,
          cost: form.kind === "service" ? computedServiceCost : (Number(form.cost) || 0),
          taxable: !!form.taxable,
          category: form.category || "",
          sku: form.sku || "",
          stockOnHand: form.kind === "material" ? (Number(form.stockOnHand) || 0) : 0,
          lowStockThreshold: form.kind === "material" && form.lowStockThreshold !== "" ? Number(form.lowStockThreshold) : null,
          memberExempt: !!form.memberExempt,
          memberPrice: form.memberPrice !== "" && form.memberPrice != null ? Number(form.memberPrice) : null,
          pieceRate: form.pieceRate !== "" && form.pieceRate != null ? Number(form.pieceRate) : null,
          bom: form.kind === "service" ? cleanBom(form.bom) : [],
          ...(form.kind === "service" ? {
            laborType: form.laborType || null,
            laborRate: Number(form.laborRate) || 0,
            laborHours: Number(form.laborHours) || 0,
            laborFlat: Number(form.laborFlat) || 0,
          } : {}),
        });
        toast.success(`${created.id} created`);
        onSaved(created);
      } else {
        const patched = await pricebookApi.patch(item.id, {
          name: form.name.trim(),
          description: form.description || "",
          unit: form.unit || "each",
          unitPrice: Number(form.unitPrice) || 0,
          cost: item.kind === "service" ? computedServiceCost : (Number(form.cost) || 0),
          taxable: !!form.taxable,
          category: form.category || "",
          sku: form.sku || "",
          lowStockThreshold: item.kind === "material" && form.lowStockThreshold !== "" ? Number(form.lowStockThreshold) : null,
          memberExempt: !!form.memberExempt,
          memberPrice: form.memberPrice !== "" && form.memberPrice != null ? Number(form.memberPrice) : null,
          pieceRate: form.pieceRate !== "" && form.pieceRate != null ? Number(form.pieceRate) : null,
          ...(item.kind === "service" ? {
            bom: cleanBom(form.bom),
            laborType: form.laborType || null,
            laborRate: Number(form.laborRate) || 0,
            laborHours: Number(form.laborHours) || 0,
            laborFlat: Number(form.laborFlat) || 0,
          } : {}),
        });
        toast.success(`${patched.id} updated`);
        onSaved(patched);
      }
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete ${item.id} — ${item.name}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await pricebookApi.remove(item.id);
      toast.success(`${item.id} deleted`);
      onDeleted(item.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/55 backdrop-blur-sm p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 flex flex-col max-h-[90vh]">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[14px] font-semibold text-slate-900">
            {isCreate ? `New ${form.kind === "service" ? "Service" : "Material"}` : `${item.id} — ${item.name}`}
          </div>
          <button onClick={onClose} className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3 overflow-y-auto flex-1">          {isCreate && (
            <Field label="Type" full>
              <div className="flex gap-2">
                {["service", "material"].map((k) => (
                  <button
                    key={k}
                    onClick={() => set({ kind: k, taxable: k === "material" })}
                    data-testid={`form-kind-${k}`}
                    className={`flex-1 h-9 rounded-md border text-[12.5px] font-semibold ${
                      form.kind === k ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-white border-slate-200 text-slate-600 hover:border-blue-300"
                    }`}
                  >
                    {k === "service" ? "Service" : "Material"}
                  </button>
                ))}
              </div>
            </Field>
          )}
          <Field label="Name" full>
            <input
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
              data-testid="form-name"
              className={inputClass}
              disabled={!canEdit}
            />
          </Field>
          <Field label="Description" full>
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => set({ description: e.target.value })}
              data-testid="form-description"
              className={`${inputClass} py-2 resize-none`}
              disabled={!canEdit}
            />
          </Field>
          <Field label="Category">
            <input
              value={form.category}
              onChange={(e) => set({ category: e.target.value })}
              placeholder="HVAC, Plumbing, Electrical…"
              data-testid="form-category"
              className={inputClass}
              disabled={!canEdit}
            />
          </Field>
          <Field label="SKU">
            <input
              value={form.sku}
              onChange={(e) => set({ sku: e.target.value })}
              data-testid="form-sku"
              className={inputClass}
              disabled={!canEdit}
            />
          </Field>
          <Field label="Unit">
            <select value={form.unit} onChange={(e) => set({ unit: e.target.value })} data-testid="form-unit" className={inputClass} disabled={!canEdit}>
              {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </Field>
          <Field label="Taxable">
            <label className="flex items-center gap-2 h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.taxable}
                onChange={(e) => set({ taxable: e.target.checked })}
                data-testid="form-taxable"
                className="accent-blue-600"
                disabled={!canEdit}
              />
              <span className="text-slate-700">Apply sales tax</span>
            </label>
          </Field>
          <Field label="Unit price ($)">
            <input
              type="number"
              min={0}
              step={0.01}
              value={form.unitPrice}
              onChange={(e) => set({ unitPrice: e.target.value })}
              data-testid="form-unit-price"
              className={inputClass}
              disabled={!canEdit}
            />
          </Field>
          <Field label="Piece rate ($/unit)">
            <input
              type="number"
              min={0}
              step={0.01}
              value={form.pieceRate}
              onChange={(e) => set({ pieceRate: e.target.value })}
              data-testid="form-piece-rate"
              className={inputClass}
              placeholder="Blank = not piece-rate eligible"
              disabled={!canEdit}
            />
          </Field>
          <Field label="Cost ($)">
            {isServiceForm ? (
              <div
                data-testid="form-cost-auto"
                className={`${inputClass} flex items-center bg-slate-50 text-slate-700 font-semibold cursor-not-allowed`}
                title="Auto-calculated from materials + labor"
              >
                ${computedServiceCost.toFixed(2)}
                <span className="ml-auto text-[10.5px] font-medium text-slate-400 normal-case">auto</span>
              </div>
            ) : (
              <input
                type="number"
                min={0}
                step={0.01}
                value={form.cost}
                onChange={(e) => set({ cost: e.target.value })}
                data-testid="form-cost"
                className={inputClass}
                disabled={!canEdit}
              />
            )}
          </Field>
          <Field label="Member pricing" full>
            <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-2.5 grid grid-cols-2 gap-2.5">
              <label className="col-span-2 flex items-center gap-2 text-[12.5px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.memberExempt}
                  onChange={(e) => set({ memberExempt: e.target.checked })}
                  data-testid="form-member-exempt"
                  className="accent-violet-600"
                  disabled={!canEdit}
                />
                <span className="text-slate-700">Exclude from membership discount</span>
              </label>
              {!form.memberExempt && (
                <div className="col-span-2">
                  <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-violet-600 mb-1">
                    Fixed member price ($) <span className="text-slate-400 normal-case font-normal">— optional, overrides the % discount</span>
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={form.memberPrice ?? ""}
                    onChange={(e) => set({ memberPrice: e.target.value })}
                    placeholder="Use tier % discount"
                    data-testid="form-member-price"
                    className={inputClass}
                    disabled={!canEdit}
                  />
                </div>
              )}
            </div>
          </Field>
          {(isCreate ? form.kind === "service" : item?.kind === "service") && (
            <Field label="Labor" full>
              <LaborEditor form={laborForm} canEdit={canEdit} onChange={set} />
            </Field>
          )}
          {(isCreate ? form.kind === "service" : item?.kind === "service") && (
            <Field label="Materials used (Bill of Materials)" full>
              <BomEditor
                bom={form.bom}
                materials={materials}
                canEdit={canEdit}
                onChange={(bom) => set({ bom })}
              />
            </Field>
          )}
          {isServiceForm && (
            <Field label="Profitability" full>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3" data-testid="form-profit">
                <div className="grid grid-cols-2 gap-y-1.5 text-[13px]">
                  <span className="text-slate-500">Materials cost</span>
                  <span className="text-right font-medium text-slate-700" data-testid="form-profit-materials">${matCost.toFixed(2)}</span>
                  <span className="text-slate-500">Labor cost</span>
                  <span className="text-right font-medium text-slate-700" data-testid="form-profit-labor">${labCost.toFixed(2)}</span>
                  <span className="text-slate-600 font-semibold border-t border-emerald-200 pt-1.5">Total cost</span>
                  <span className="text-right font-semibold text-slate-900 border-t border-emerald-200 pt-1.5" data-testid="form-profit-cost">${computedServiceCost.toFixed(2)}</span>
                  <span className="text-slate-500">Unit price (charged)</span>
                  <span className="text-right font-medium text-slate-700">${(Number(form.unitPrice) || 0).toFixed(2)}</span>
                </div>
                <div className="mt-2.5 flex items-center justify-between rounded-md bg-white border border-emerald-200 px-3 py-2">
                  <span className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700">Profit</span>
                  <div className="flex items-baseline gap-2">
                    <span className={`text-[17px] font-bold ${profit >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="form-profit-amount">${profit.toFixed(2)}</span>
                    <span className={`text-[13px] font-semibold ${profit >= 0 ? "text-emerald-500" : "text-rose-500"}`} data-testid="form-profit-pct">{pct.toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            </Field>
          )}
          {(isCreate ? form.kind === "material" : item?.kind === "material") && (
            <>
              <Field label={isCreate ? "Initial stock" : `Current stock: ${item.stockOnHand}`}>
                {isCreate ? (
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={form.stockOnHand}
                    onChange={(e) => set({ stockOnHand: e.target.value })}
                    data-testid="form-stock"
                    className={inputClass}
                  />
                ) : (
                  <Button
                    variant="outline"
                    onClick={onAdjustStock}
                    disabled={!canEdit}
                    data-testid="form-adjust-stock-btn"
                    className="h-9 w-full text-[13px] gap-1.5"
                  >
                    <ArrowDownUp className="h-3.5 w-3.5" /> Adjust stock…
                  </Button>
                )}
              </Field>
              <Field label="Low-stock threshold">
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={form.lowStockThreshold ?? ""}
                  onChange={(e) => set({ lowStockThreshold: e.target.value })}
                  placeholder="Default: 10"
                  data-testid="form-low-stock-threshold"
                  className={inputClass}
                  disabled={!canEdit}
                />
              </Field>
            </>
          )}
          {error && <div className="col-span-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-[12.5px] text-red-700">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-between gap-2 bg-slate-50/40 shrink-0">
          {!isCreate && canEdit && (
            <Button variant="outline" onClick={del} disabled={busy} data-testid="form-delete" className="h-9 text-[13px] text-rose-600 hover:bg-rose-50 hover:border-rose-300 border-slate-200">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose} className="h-9 text-[13px]">{canEdit ? "Cancel" : "Close"}</Button>
          {canEdit && (
            <Button
              onClick={submit}
              disabled={busy || !form.name.trim()}
              data-testid="form-submit"
              className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px] font-semibold"
            >
              {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</> : (isCreate ? <><Plus className="h-4 w-4 mr-1.5" /> Create</> : <><Pencil className="h-4 w-4 mr-1.5" /> Save</>)}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function AdjustStockDialog({ item, onClose, onAdjusted }) {
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    const d = parseFloat(delta);
    if (!d || d === 0) { setError("Delta must be a non-zero number (+ to add, − to remove)"); return; }
    setError("");
    setBusy(true);
    try {
      const updated = await pricebookApi.adjustStock(item.id, d, reason);
      toast.success(`Stock ${d > 0 ? "+" : ""}${d} · new total ${updated.stockOnHand}`);
      // Notify the sidebar badge (and any other listener) that stock changed.
      try { window.dispatchEvent(new CustomEvent("pricebook-stock-changed")); } catch (_) { /* SSR safety */ }
      onAdjusted(updated);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/55 backdrop-blur-sm p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[14px] font-semibold text-slate-900">Adjust stock — {item.id}</div>
          <button onClick={onClose} className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-[12.5px] text-slate-600">
            <strong className="text-slate-900">{item.name}</strong> · current stock: <span className="font-bold">{item.stockOnHand}</span>
          </div>
          <Field label="Delta (+ to receive, − to deplete)">
            <input
              type="number"
              step={1}
              value={delta}
              onChange={(e) => setDelta(e.target.value)}
              placeholder="e.g. 24 or -5"
              data-testid="adjust-delta"
              className={inputClass}
              autoFocus
            />
          </Field>
          <Field label="Reason">
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Received from supplier, damaged, audit correction…"
              data-testid="adjust-reason"
              className={inputClass}
            />
          </Field>
          {error && <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-[12.5px] text-red-700">{error}</div>}
          {(item.stockLog || []).length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Recent adjustments</div>
              <div className="max-h-32 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100">
                {(item.stockLog || []).slice(-5).reverse().map((l) => (
                  <div key={l.id} className="px-2 py-1.5 text-[11.5px] flex items-center justify-between">
                    <span className={`font-bold ${l.delta > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      {l.delta > 0 ? "+" : ""}{l.delta}
                    </span>
                    <span className="text-slate-500 truncate flex-1 mx-2">{l.reason || "—"}</span>
                    <span className="text-slate-400 text-[10.5px]">
                      → {l.newStock} · {new Date(l.at).toLocaleDateString([], { month: "short", day: "numeric" })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/40">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !delta}
            data-testid="adjust-submit"
            className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] font-semibold"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
          </Button>
        </div>
      </div>
    </div>
  );
}

const inputClass =
  "w-full h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:bg-slate-50 disabled:text-slate-500 transition-colors";

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
